import type { SupportedLocale } from "@/lib/i18n";

/** Dev demo mode — gated by VITE_DEMO_ENABLED=1 in apps/web/.env.local */
export const demoConfig = {
  enabled: import.meta.env.VITE_DEMO_ENABLED === "1",
  handle: (import.meta.env.VITE_DEMO_HANDLE as string | undefined)?.trim() || "demo",
  email: (import.meta.env.VITE_DEMO_EMAIL as string | undefined)?.trim().toLowerCase() || "",
  password: (import.meta.env.VITE_DEMO_PASSWORD as string | undefined) || "",
} as const;

export function isDemoModeConfigured(): boolean {
  return demoConfig.enabled && Boolean(demoConfig.email && demoConfig.password);
}

export function publicDemoSitePath(): string {
  return `/w/${demoConfig.handle}`;
}

/** Per-locale demo credentials — falls back to no config when locale-specific vars are absent. */
export function demoConfigForLocale(locale: SupportedLocale): {
  email: string;
  password: string;
  handle: string;
} {
  if (!demoConfig.enabled) return { email: "", password: "", handle: "" };

  if (locale === "en") {
    const email =
      (import.meta.env.VITE_DEMO_EMAIL_EN as string | undefined)?.trim().toLowerCase() || "";
    const password = (import.meta.env.VITE_DEMO_PASSWORD_EN as string | undefined) || "";
    const handle = (import.meta.env.VITE_DEMO_HANDLE_EN as string | undefined)?.trim() || "demo-en";
    return { email, password, handle };
  }

  if (locale === "de") {
    const email =
      (import.meta.env.VITE_DEMO_EMAIL_DE as string | undefined)?.trim().toLowerCase() || "";
    const password = (import.meta.env.VITE_DEMO_PASSWORD_DE as string | undefined) || "";
    const handle = (import.meta.env.VITE_DEMO_HANDLE_DE as string | undefined)?.trim() || "demo-de";
    return { email, password, handle };
  }

  // Polish (default)
  return {
    email: demoConfig.email,
    password: demoConfig.password,
    handle: demoConfig.handle,
  };
}

/** True when a demo account for the given locale is fully configured. */
export function isDemoModeConfiguredForLocale(locale: SupportedLocale): boolean {
  const cfg = demoConfigForLocale(locale);
  return Boolean(cfg.email && cfg.password);
}

/** All configured demo emails (for identity checks). */
export function allDemoEmails(): readonly string[] {
  const emails: string[] = [];
  for (const locale of ["pl", "en", "de"] as const) {
    const cfg = demoConfigForLocale(locale);
    if (cfg.email) emails.push(cfg.email);
  }
  return emails;
}
